import React, { useEffect, useState } from "react";
import LeadOtpVerification from "@/components/common/LeadOtpVerification";

const initialState = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  seminarDate: "",
  seminarTiming: "",
  venue: "",
  mapLocation: "",
};

const WEBHOOK_URL =
  "https://services.leadconnectorhq.com/hooks/dnei3jPcUXkrtRpBZALC/webhook-trigger/489447ee-8ed3-43de-837f-cccf22cc5d2a";

const SEMINAR_ID = "neet-ug-2026-summit";

function formatSeminarDate(dateText) {
  if (!dateText) return "";

  // Example input: "28 June 2026"
  // Output: "28-06-2026"
  const monthMap = {
    january: "01",
    february: "02",
    march: "03",
    april: "04",
    may: "05",
    june: "06",
    july: "07",
    august: "08",
    september: "09",
    october: "10",
    november: "11",
    december: "12",
  };

  const parts = String(dateText).trim().split(/\s+/);

  if (parts.length !== 3) return dateText;

  const [day, month, year] = parts;
  const monthNumber = monthMap[String(month).toLowerCase()];

  if (!monthNumber) return dateText;

  return `${String(day).padStart(2, "0")}-${monthNumber}-${year}`;
}

function formatSeminarTime(timeText) {
  if (!timeText) return "";

  // Example input: "11:00 AM IST"
  // Output: "11:00 AM"
  return String(timeText).replace(/\s*IST\s*$/i, "").trim();
}

function buildVenue(venueShort, address) {
  const venue = String(venueShort || "").trim();
  const fullAddress = String(address || "").trim();

  if (venue && fullAddress) {
    return `${venue}, ${fullAddress}`;
  }

  return venue || fullAddress || "";
}

function ThankYouModal({ onClose }) {
  return (
    <div
      className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
      >
        ✕
      </button>

      <div className="flex flex-col items-center text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-xl text-green-600">
          ✓
        </div>

        <h3 className="mt-3 text-xl font-bold text-gray-900">Thank You!</h3>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          Your registration has been submitted successfully.
        </p>

        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-xl bg-[#ff9245] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#ff9245]/90"
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default function SeminarRegistration({ onSubmit, onClose }) {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [showThankYou, setShowThankYou] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  useEffect(() => {
    const fetchSeminarConfig = async () => {
      try {
        setConfigLoading(true);

        const baseUrl = process.env.NEXT_PUBLIC_ADMIN_URL || "";

        const response = await fetch(
          `${baseUrl}/api/seminar-config/${SEMINAR_ID}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            cache: "no-store",
          }
        );

        const json = await response.json();

        if (!response.ok) {
          console.error("Seminar config API error:", response.status, json);
          return;
        }

        const cfg = json?.data || json;

        setForm((prev) => ({
          ...prev,
          seminarDate: formatSeminarDate(cfg?.date),
          seminarTiming: formatSeminarTime(cfg?.time),
          venue: buildVenue(cfg?.venueShort, cfg?.address),
          mapLocation: cfg?.mapsUrl || "",
        }));
      } catch (error) {
        console.error("Seminar config fetch error:", error);
      } finally {
        setConfigLoading(false);
      }
    };

    fetchSeminarConfig();
  }, []);

  const validate = () => {
    const newErrors = {};

    if (!form.firstName.trim()) newErrors.firstName = "First Name is required";
    if (!form.lastName.trim()) newErrors.lastName = "Last Name is required";
    if (!form.phone.trim()) newErrors.phone = "Phone is required";
    if (!form.email.trim()) newErrors.email = "Email is required";

    if (!form.seminarDate) {
      newErrors.seminarDate = "Seminar Date is required";
    }

    if (!form.seminarTiming.trim()) {
      newErrors.seminarTiming = "Seminar Timing is required";
    }

    if (!form.venue.trim()) {
      newErrors.venue = "Venue is required";
    }

    if (!form.mapLocation.trim()) {
      newErrors.mapLocation = "Map Location is required";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const resetUserFieldsOnly = () => {
    setForm((prev) => ({
      ...prev,
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      // Keep dynamic seminar fields as they are
      seminarDate: prev.seminarDate,
      seminarTiming: prev.seminarTiming,
      venue: prev.venue,
      mapLocation: prev.mapLocation,
    }));

    setErrors({});
    setIsOtpVerified(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!isOtpVerified) {
      alert("Please verify your phone via WhatsApp OTP before submitting.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        email: form.email,
        seminarDate: form.seminarDate,
        seminarTiming: form.seminarTiming,
        venue: form.venue,
        mapLocation: form.mapLocation,
      };

      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to submit registration");
      }

      if (onSubmit) onSubmit(payload);

      localStorage.setItem("seminarRegistered", "true");

      resetUserFieldsOnly();
      setShowThankYou(true);
    } catch (error) {
      console.error("Registration submit error:", error);
      alert("Something went wrong while submitting the form.");
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "mt-1 w-full rounded-lg border px-3 py-2.5 text-sm text-gray-900 outline-none transition";

  const inputNormal =
    "border-gray-200 bg-white placeholder:text-gray-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-100";

  const inputError =
    "border-red-500 bg-red-50 placeholder:text-gray-400 focus:border-red-500 focus:ring-4 focus:ring-red-100";

  if (showThankYou) {
    return <ThankYouModal onClose={onClose} />;
  }

  return (
    <div
      className="relative w-full max-w-xl rounded-2xl bg-white shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
      >
        ✕
      </button>

      <div className="max-h-[85vh] overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
        <div className="mb-4 text-center">
          <h3 className="text-xl underline sm:text-2xl font-bold text-[#ff9245]">
            Reserve your seat
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Fill in your details to register.
          </p>
        </div>

        <div className="mb-4 grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-orange-100 bg-orange-50 px-2.5 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#ff9245]">
              Date
            </p>
            <p className="mt-1 text-xs font-bold text-gray-900">
              {configLoading ? "Loading..." : form.seminarDate || "TBA"}
            </p>
          </div>

          <div className="rounded-lg border border-orange-100 bg-orange-50 px-2.5 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#ff9245]">
              Time
            </p>
            <p className="mt-1 text-xs font-bold text-gray-900">
              {configLoading ? "Loading..." : form.seminarTiming || "TBA"}
            </p>
          </div>

          <div className="rounded-lg border border-orange-100 bg-orange-50 px-2.5 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#ff9245]">
              Venue
            </p>
            <p className="mt-1 text-xs font-bold text-gray-900">
              {configLoading
                ? "Loading..."
                : form.venue
                ? "New Delhi"
                : "TBA"}
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <div>
            <label className="text-xs font-semibold text-gray-700">
              First Name
            </label>
            <input
              name="firstName"
              placeholder="Enter first name"
              value={form.firstName}
              onChange={handleChange}
              className={`${inputBase} ${
                errors.firstName ? inputError : inputNormal
              }`}
            />
            {errors.firstName && (
              <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">
              Last Name
            </label>
            <input
              name="lastName"
              placeholder="Enter last name"
              value={form.lastName}
              onChange={handleChange}
              className={`${inputBase} ${
                errors.lastName ? inputError : inputNormal
              }`}
            />
            {errors.lastName && (
              <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">
              Phone Number
            </label>
            <LeadOtpVerification
              phone={form.phone}
              otpEnabled={false}
              onPhoneChange={(value) => {
                setForm((prev) => ({ ...prev, phone: value }));
                setErrors((prev) => ({ ...prev, phone: "" }));
              }}
              inlinePhoneField
              phonePlaceholder="Enter phone number"
              phoneInputClassName={`${inputBase} ${
                errors.phone ? inputError : inputNormal
              }`}
              onVerifiedChange={setIsOtpVerified}
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">
              Email Address
            </label>
            <input
              name="email"
              type="email"
              placeholder="Enter email address"
              value={form.email}
              onChange={handleChange}
              className={`${inputBase} ${
                errors.email ? inputError : inputNormal
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">
              Seminar Date
            </label>
            <input
              name="seminarDate"
              type="text"
              placeholder="dd-mm-yyyy"
              value={form.seminarDate}
              readOnly
              className={`${inputBase} bg-gray-50 ${
                errors.seminarDate ? inputError : inputNormal
              }`}
            />
            {errors.seminarDate && (
              <p className="mt-1 text-xs text-red-500">
                {errors.seminarDate}
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">
              Seminar Timing
            </label>
            <input
              name="seminarTiming"
              value={form.seminarTiming}
              readOnly
              className={`${inputBase} bg-gray-50 ${
                errors.seminarTiming ? inputError : inputNormal
              }`}
            />
            {errors.seminarTiming && (
              <p className="mt-1 text-xs text-red-500">
                {errors.seminarTiming}
              </p>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-gray-700">
              Venue
            </label>
            <textarea
              name="venue"
              rows={2}
              value={form.venue}
              readOnly
              className={`${inputBase} resize-none bg-gray-50 ${
                errors.venue ? inputError : inputNormal
              }`}
            />
            {errors.venue && (
              <p className="mt-1 text-xs text-red-500">{errors.venue}</p>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-gray-700">
              Map Location
            </label>
            <input
              name="mapLocation"
              value={form.mapLocation}
              readOnly
              className={`${inputBase} bg-gray-50 ${
                errors.mapLocation ? inputError : inputNormal
              }`}
            />
            {errors.mapLocation && (
              <p className="mt-1 text-xs text-red-500">
                {errors.mapLocation}
              </p>
            )}
          </div>

          <div className="sm:col-span-2 pt-1">
            <button
              type="submit"
              disabled={loading || configLoading}
              className="w-full rounded-lg bg-[#ff9245] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#ff9245]/90 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading
                ? "Submitting..."
                : configLoading
                ? "Loading seminar details..."
                : "Register"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}